import React, { useState } from "react";
import classes from "./ClaimModalStyles.module.scss";
import { useLocalStorageSerializeKey } from "../../../lib/localStorage";
import { ethers } from "ethers";
import RewardRouter from "../../../abis/RewardRouter.json";
import { callContract } from "../../../lib/contracts";
import Checkbox from "../../Checkbox/Checkbox";
import { t, Trans } from "@lingui/macro";
import { useWeb3React } from "@web3-react/core";
import { useChainId } from "../../../lib/chains";
import { ModalBase } from "../ModalBase/ModalBase";
import { ActionButton } from "../../Buttons/ActionButton/ActionButton";
import Switch from "@mui/material/Switch";

type Props = {
  onClose: () => void;
  rewardRouterAddress: string;
  setPendingTxns: any;
  nativeTokenSymbol: string;
  wrappedTokenSymbol: string;
};

export const ClaimModal = ({
  onClose,
  rewardRouterAddress,
  setPendingTxns,
  nativeTokenSymbol,
  wrappedTokenSymbol,
}: Props) => {
  const { library } = useWeb3React();
  const { chainId } = useChainId();

  const [isClaiming, setIsClaiming] = useState(false);
  const [shouldClaimCad, setShouldClaimCad] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-claim-should-claim-cad"],
    true
  );
  const [shouldClaimEsCad, setShouldClaimEsCad] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-claim-should-claim-es-cad"],
    true
  );
  const [shouldClaimWeth, setShouldClaimWeth] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-claim-should-claim-weth"],
    true
  );
  const [shouldConvertWeth, setShouldConvertWeth] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-claim-should-convert-weth"],
    true
  );

  const [shouldConvertWCanto, setShouldConvertWCanto] = useState(!!shouldConvertWeth);

  const getPrimaryText = () => {
    if (isClaiming) {
      return t`Claiming...`;
    }
    return t`Claim`;
  };

  const onClickPrimary = () => {
    setIsClaiming(true);

    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter.abi, library.getSigner());

    callContract(
      chainId,
      contract,
      "handleRewards",
      [
        shouldClaimCad,
        false, // shouldStakeCad
        shouldClaimEsCad,
        false, // shouldStakeEsCad
        false, // shouldStakeMultiplierPoints
        shouldClaimWeth,
        shouldConvertWeth,
      ],
      {
        sentMsg: t`Claim submitted.`,
        failMsg: t`Claim failed.`,
        successMsg: t`Claim completed!`,
        setPendingTxns,
      }
    )
      .then(async (res) => {
        onClose();
      })
      .finally(() => {
        setIsClaiming(false);
      });
  };

  const toggleConvertWeth = (value) => {
    if (value) {
      setShouldClaimWeth(true);
    }
    setShouldConvertWeth(value);
    setShouldConvertWCanto((prevState) => !prevState);
  };

  return (
    <ModalBase onClose={onClose} title={t`Claim Rewards`}>
      <div className={classes.wrapper}>
        <Checkbox isChecked={shouldClaimCad} className={classes.checkBox} setIsChecked={setShouldClaimCad}>
          <Trans>CAD</Trans>
        </Checkbox>

        <Checkbox isChecked={shouldClaimEsCad} className={classes.checkBox} setIsChecked={setShouldClaimEsCad}>
          <Trans>esCAD</Trans>
        </Checkbox>

        <div className={classes.shouldConvertWcantoWrapper}>
          <Checkbox
            isChecked={shouldClaimWeth}
            className={classes.checkBox}
            setIsChecked={setShouldClaimWeth}
            disabled={shouldConvertWeth}
          >
            {wrappedTokenSymbol}
          </Checkbox>
          {shouldClaimWeth && (
            <div className={classes.shouldConvertWcantoWrapper}>
              <Switch
                checked={shouldConvertWCanto}
                onChange={() => toggleConvertWeth(!shouldConvertWCanto)}
                inputProps={{ "aria-label": "controlled" }}
                className={shouldConvertWCanto ? classes.switch : classes.switchUnchecked}
              />
              <span className={classes.convertSwitchLabel}><Trans>Convert</Trans> {wrappedTokenSymbol} <Trans>to</Trans> {nativeTokenSymbol}</span>
            </div>
          )}
        </div>
        <ActionButton
          onClick={onClickPrimary}
          type={"outlined"}
          buttonText={getPrimaryText()}
          disabled={isClaiming}
          className={classes.button}
        />
      </div>
    </ModalBase>
  );
};
